import React, { useState } from 'react';
import { makeStyles  } from '@material-ui/core/styles';
import { TwitterPicker } from 'react-color';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';

import EditIcon from '@material-ui/icons/Edit';

import EditImageDialog from 'app/components/dialogs/EditImageDialog';
import EditFileDialog from 'app/components/drive/EditFileDialog';
import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';

import { paper } from 'app/styles/core';

import DefaultColors from 'app/utils/MaterialColors';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        marginBottom: theme.spacing(3),
        position: 'relative'
    },
    logoOuterContainer: {
        textAlign: 'center',
        paddingTop: theme.spacing(3),
        position: 'relative'
    },
    logoInnerContainer: {
        display: 'inline-block',
        position: 'relative'
    },
    logo: {
        maxWidth: 240,
        maxHeight: 240,
        display: 'block'
    },
    iconContainer: {
        textAlign: 'center',
        marginTop: theme.spacing(3)
    },
    icon: {
        maxWidth: 40,
        maxHeight: 40,
        display: 'block'
    },
    colorContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(3)
    },
    colorSwatchContainer: {
        display: 'inline-block'
    },
    colorSwatch: {
        height: 32,
        width: 32,
        marginRight: theme.spacing(3),
        verticalAlign: 'middle',
        display: 'inline-block',
        borderRadius: theme.shape.borderRadius
    },
    marginLeft: {
        marginLeft: theme.spacing(1)
    },
    marginBottom: {
        marginBottom: theme.spacing(1)
    },
    editButton: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        fontSize: 50,
        opacity: 0,
        transition: 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        color: '#ffffff',
        borderRadius: 0,

        ['&:hover']: {
            opacity: 0.8,
            backgroundColor: 'rgba(0, 0, 0, 0.24)'
        }
    },
    saveButtonContainer: {
        marginTop: theme.spacing(3)
    },
    saveButton: {
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
    },
}));

export default function Branding(props) {

    const { app, hydrateApp } = useApp();
    const api = useApi();
    const classes = useStyles();

    const logo = app.club.branding.logo;
    const icon = app.club.branding.icon;

    const [ editImageDialogOpen, setEditImageDialogOpen ] = useState(false);
    const [ editFileDialogOpen, setEditFileDialogOpen ] = useState(false);

    const [ editingFile, setEditingFile ] = useState(null);
    const [ editingType, setEditingType ] = useState(null);

    const [ branding, setBranding] = useState({
        acronym: app.club.branding.acronym || "",
        acronym_short: app.club.branding.acronym_short || ""
    });
    const [ color, setColor ] = useState(app.club.branding.primary_color);
    const [ editColor, setEditColor ] = useState(false);

    const [ brandingLoading, setBrandingLoading ] = useState(false);
    const [ logoLoading, setLogoLoading ] = useState(false);
    const [ iconLoading, setIconLoading ] = useState(false);
    const [ colorLoading, setColorLoading ] = useState(false);

    const [ errors, setErrors ] = useState({});

    // handlers

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setBranding({ ...branding, [name]: value });
    };

    // Edit File Dialog Functions

    const openEditFileDialog = (file = null, type) => {
        setEditingFile(file);
        setEditFileDialogOpen(true);
        setEditingType(type)
    }

    const closeEditFileDialog = () => {
        setEditFileDialogOpen(false);
        setEditingType(null)
    }

    const closeEditFileDialogAndRefresh = file => {
        closeEditFileDialog();

        if (file && file.id) {
            switch (editingType) {
                case 'logo':
                    postLogo(file.id);
                    break;
                case 'icon':
                    postIcon(file.id);
            }
        }
    }

    // Edit Image Dialog Functions

    const openEditImageDialog = () => {
        setEditImageDialogOpen(true);
    }

    const closeEditImageDialog = () => {
        setEditImageDialogOpen(false);
    }

    const closeEditImageDialogAndRefresh = () => {
        closeEditImageDialog();
        hydrateApp(true);
    }

    // Editing Color Functions

    const toggleEditColor = () => {
        setEditColor(!editColor);
    }

    const handleColor = color => {
        setColor(color.hex);
    }

    // Post Functions

    const postBranding = () => {
        setBrandingLoading(true);

        api.post('/admin/club/branding', branding)
            .then(() => hydrateApp(true))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setBrandingLoading(false));
    }

    const postLogo = id => {
        setLogoLoading(true);

        api.post('/admin/club/branding/logo', {logo_id: id})
            .then(() => hydrateApp(true))
            .finally(() => setLogoLoading(false));
    }

    const postIcon = id => {
        setIconLoading(true);

        api.post('/admin/club/branding/icon', {icon_id: id})
            .then(() => hydrateApp(true))
            .finally(() => setIconLoading(false));
    }

    const postColor = () => {
        setColorLoading(true);

        api.post('/admin/club/branding/color', {color: color})
            .then(() => setEditColor(false))
            .finally(() => setColorLoading(false));
    }

    const deleteLogo = () => {
        setLogoLoading(true);

        api.delete('/admin/club/branding/logo')
            .then(() => hydrateApp(true))
            .finally(() => setLogoLoading(false));
    }

    const deleteIcon = () => {
        setIconLoading(true);

        api.delete('/admin/club/branding/icon')
            .then(() => hydrateApp(true))
            .finally(() => setIconLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field][0];
        return "";
    }

    const getAcronymMessage = () => {
        if (isError('acronym')) return errors[field][0];
        return "Used to display your club name in locations with little room like large avatars";
    }

    const getAcronymShortMessage = () => {
        if (isError('acronym_short')) return errors[field][0];
        return "Used to display your club name in small spaces like avatars and icons.";
    }

    return (
        <form noValidate autoComplete="off">
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h2" gutterBottom>
                            Branding
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Adjust your clubs branding to accurately respresent your club to your members and guests. Upload your club logo, modify colors and more.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={brandingLoading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            General Settings
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="input-acronym"
                            label="Club Acronym"
                            value={branding.acronym}
                            onChange={handleChange('acronym')}
                            fullWidth
                            error={isError('acronym')}
                            helperText={getAcronymMessage('acronym')}
                            variant='outlined'
                            required
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="input-short-acronym"
                            label="Club Short Acronym"
                            value={branding.acronym_short}
                            onChange={handleChange('acronym_short')}
                            fullWidth
                            error={isError('acronym_short')}
                            helperText={getAcronymMessage('acronym_short')}
                            variant='outlined'
                            required
                            margin="normal"
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.saveButtonContainer}>
                    <Button
                        disabled={brandingLoading}
                        variant="contained"
                        color="primary"
                        onClick={postBranding}
                        className={classes.saveButton}
                        size="small"
                        >
                        save
                    </Button>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={logoLoading} />
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    className={classes.marginBottom}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            Club Logo
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => openEditFileDialog(logo, 'logo')}
                            >
                            {logo ? 'Edit Logo Details' : 'Upload Logo'}
                        </Button>
                        {logo &&
                            <RedButton
                                variant="outlined"
                                onClick={deleteLogo}
                                className={classes.marginLeft}
                                >
                                Remove Logo
                            </RedButton>
                        }
                    </Grid>
                </Grid>
                <Typography color="textSecondary" gutterBottom>
                    Your logo is used throughout the application, in emails and more. For the best experience, use a png with a clear background and no larger than 400 pixels.
                </Typography>
                {(logo && logo.full_url) &&
                    <div className={classes.logoOuterContainer}>
                        <div className={classes.logoInnerContainer}>
                            <img src={logo.full_url} title="Club Logo" className={classes.logo}/>
                            <IconButton className={classes.editButton} onClick={openEditImageDialog}>
                                <EditIcon fontSize='inherit' />
                            </IconButton>
                        </div>
                    </div>
                }
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={iconLoading} />
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    className={classes.marginBottom}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            Club Icon
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => openEditFileDialog(icon, 'icon')}
                            >
                            {icon ? 'Edit Icon Details' : 'Upload Icon'}
                        </Button>
                        {icon &&
                            <RedButton
                                variant="outlined"
                                onClick={deleteIcon}
                                className={classes.marginLeft}
                                >
                                Remove Icon
                            </RedButton>
                        }
                    </Grid>
                </Grid>
                <Typography color="textSecondary" gutterBottom>
                    Your icon is used in browser tabs, bookmark bars and on mobile devices to represent your application. For the best experience use a square jpeg that is at least 512 × 512 pixels.
                </Typography>
                {(icon && icon.full_url) &&
                    <div className={classes.iconContainer}>
                        <img src={icon.full_url} title="Club Icon" className={classes.icon}/>
                    </div>
                }
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={colorLoading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            Primary Club Color
                        </Typography>
                    </Grid>
                    <Grid item>
                        <div className={classes.colorSwatchContainer}>
                            <span className={classes.colorSwatch} style={{backgroundColor: color}}/>
                            <Typography variant="subtitle2" component="span">{color}</Typography>
                        </div>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            onClick={toggleEditColor}
                            >
                            {editColor ? 'Cancel' : 'Edit Color'}
                        </Button>
                        {editColor &&
                            <Button
                                variant="contained"
                                onClick={postColor}
                                color="primary"
                                className={classes.marginLeft}
                                >
                                Save Color
                            </Button>
                        }
                    </Grid>
                </Grid>
                {editColor &&
                    <div className={classes.colorContainer}>
                        <TwitterPicker
                            width={312}
                            colors={DefaultColors}
                            color={color}
                            triangle="hide"
                            onChangeComplete={handleColor}
                            />
                    </div>
                }
            </Paper>
            <EditFileDialog
                open={editFileDialogOpen}
                onSave={closeEditFileDialogAndRefresh}
                onClose={closeEditFileDialog}
                initFile={editingFile}
                initGroupId={null}
            />
            {(logo && logo.url) &&
                <EditImageDialog
                    file={logo}
                    serverUrl="/admin/file/edit/image"
                    open={editImageDialogOpen}
                    onSave={closeEditImageDialogAndRefresh}
                    onClose={closeEditImageDialog}
                />
            }
        </form>
    )
}
